import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Article, articles, categoryTranslations } from '../thi/thi-articles.data';
import { Language, getDirection, translations } from '../thi/thi-translations';
import { normalizeThiLang } from '../thi/thi-lang.util';

const UI = {
  notFound: {
    ar: 'المقال غير موجود',
    en: 'Article Not Found',
    he: 'המאמר לא נמצא',
  },
  related: {
    ar: 'مقالات ذات صلة',
    en: 'Related Articles',
    he: 'מאמרים קשורים',
  },
  ctaTitle: {
    ar: 'جاهز لتطوير أعمالك؟',
    en: 'Ready to Grow Your Business?',
    he: 'מוכן לשפר את העסק שלך?',
  },
  ctaDesc: {
    ar: 'جرّب THI Invoices مجانًا واكتشف كيف يمكننا مساعدتك',
    en: 'Try THI Invoices free and discover how we can help',
    he: 'נסה את THI Invoices בחינם וגלה כיצד אנחנו יכולים לעזור',
  },
} as const;

@Component({
  selector: 'app-thi-article-detail',
  templateUrl: './thi-article-detail.component.html',
  styleUrls: ['./thi-article-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThiArticleDetailComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  landingLang: Language = 'ar';
  dir: 'rtl' | 'ltr' = 'rtl';
  t = translations.ar;

  article: Article | null = null;
  safeContent: SafeHtml | null = null;
  relatedArticles: Article[] = [];

  constructor(
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const raw = localStorage.getItem('curentLang') || 'ar';
    this.applyLang(normalizeThiLang(raw));
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((pm) => {
      const id = pm.get('id');
      this.article = id ? articles.find((a) => a.id === id) ?? null : null;
      if (this.article) {
        this.safeContent = this.sanitizer.bypassSecurityTrustHtml(
          this.article.content[this.landingLang],
        );
        this.relatedArticles = articles
          .filter((a) => a.category === this.article!.category && a.id !== this.article!.id)
          .slice(0, 2);
      } else {
        this.safeContent = null;
        this.relatedArticles = [];
      }
      this.cdr.markForCheck();
    });
  }

  private applyLang(l: Language): void {
    this.landingLang = l;
    this.dir = getDirection(l);
    this.t = translations[l];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  text(key: keyof typeof UI): string {
    return UI[key][this.landingLang];
  }

  categoryLabel(a: Article): string {
    return categoryTranslations[this.landingLang][a.category];
  }

  categoryIconClass(cat: Article['category']): string {
    const m: Record<Article['category'], string> = {
      business: 'fa fa-briefcase',
      finance: 'fa fa-line-chart',
      productivity: 'fa fa-bolt',
      technology: 'fa fa-microchip',
    };
    return m[cat];
  }

  get arrowBackIcon(): string {
    return this.dir === 'rtl' ? 'fa fa-arrow-right' : 'fa fa-arrow-left';
  }

  get arrowIcon(): string {
    return this.dir === 'rtl' ? 'fa fa-arrow-left' : 'fa fa-arrow-right';
  }
}
